import { useState } from 'react';
import { useKaydedilmemisIcerik } from '../shell/KaydedilmemisIcerik';
import {
  Alert,
  Button,
  Checkbox,
  FileButton,
  Group,
  Select,
  Stack,
  Switch,
  Table,
  Text,
  TextInput,
  Textarea,
} from '@mantine/core';
import { Link } from 'react-router';
import {
  KURUL_KARAR_SONUC_ETIKETI,
  kurulKararSonucu,
  KURUL_UYE_ROL_ETIKETI,
  KURUL_UYE_TIPI_ETIKETI,
  KURUL_UYE_TIPLERI,
} from '@isg/validation';
import type {
  GundemMaddesi,
  Karar,
  KurulDetay,
  KurulUyeTipi,
  ToplantiDetay,
} from '@isg/validation';
import {
  useCiktiIndir,
  useGundemMaddesiEkle,
  useGundemMaddesiSil,
  useGundemSecenekleri,
  useKararDofAc,
  useKararDuzelt,
  useKararYaz,
  useKatilimYaz,
  useToplantiKapat,
  useToplantiNushaSil,
  useToplantiNushaYukle,
  useAtanabilirler,
  useGorevlendirmeEkle,
  useSahaGunu,
} from '../api/queries';
import {
  Bolum,
  KapiSebebi,
  Rozet,
  SatirEylemleri,
  SatirSilmeSebebi,
  SolukSatir,
} from '../sozlesme';
import { ANLAM } from '../theme';
import { tarihGoster, zamanGoster } from '../lib/format';

/**
 * İSG KURULU — DETAY BÖLÜMLERİ (Kapsam §28; sözleşme P1-a · P2 · P11).
 *
 * ⚠️ **DÜĞME GÖRÜNÜRLÜĞÜ SUNUCUDAN** (`yetkiler` — §2.40): ekran şapka ya da
 * yeter sayı türetmez; *"karar yazılamaz"* cümlesi `kararEngeli`den gelir.
 * ⚠️ Hata sessizce yutulmaz: her yazma kancasının hatası `role="alert"`
 * bandında görünür (D1).
 */

// ── ÜYELER (C2) — görevlendirmeden, eksik damgası görevlendirme motorunun ──

export function UyelerBolumu({ kurul }: { kurul: KurulDetay }): React.JSX.Element {
  return (
    <Bolum
      baslik="Üyeler"
      aciklama="Üyelik “Kurul üyesi” görevlendirmesidir (kişi kartından ya da buradan); eksik damgası görevlendirme kuralından gelir."
      testId="kurul-uyeler"
    >
      {kurul.yetkiler.uyeEkleyebilir && kurul.uyelikTuruId && (
        <UyeEkleHatasi>
          {(setHata) => (
            <UyeEkleSatiri kurul={kurul} uyelikTuruId={kurul.uyelikTuruId!} onHata={setHata} />
          )}
        </UyeEkleHatasi>
      )}
      {/* P13 · B1/2 — kapı GÖREVLENDİRME ucunun kapısıdır (şapka → kişi bahçesi). */}
      <KapiSebebi
        durum={kurul.yetkiler.uyeEklemeKapisi}
        nesne="Kurul üyesini"
        fiil="ekler"
        testId="kurul-uye-yazamama-sebebi"
      />
      <Table data-testid="kurul-uye-tablosu">
        <Table.Thead>
          <Table.Tr>
            <Table.Th>Ad Soyad</Table.Th>
            <Table.Th>Üye tipi</Table.Th>
            <Table.Th w={110}>Rol</Table.Th>
            <Table.Th w={120}>Başlangıç</Table.Th>
            <Table.Th w={160}>Durum</Table.Th>
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          {kurul.uyeler.length === 0 && (
            <Table.Tr>
              <Table.Td colSpan={5}>
                <Text size="sm" c="dimmed" data-testid="kurul-uye-yok">
                  Henüz üye yok.
                </Text>
              </Table.Td>
            </Table.Tr>
          )}
          {kurul.uyeler.map((u) => (
            <Table.Tr key={u.designationId} data-testid={`kurul-uye-${u.personId}`}>
              <Table.Td>
                <Link to={`/calisanlar/${u.personId}`}>{u.adSoyad}</Link>
              </Table.Td>
              <Table.Td>{KURUL_UYE_TIPI_ETIKETI[u.uyeTipi]}</Table.Td>
              <Table.Td>
                <Rozet anlam={u.rol === 'uye' ? 'bilgi' : 'olumlu'}>
                  {KURUL_UYE_ROL_ETIKETI[u.rol]}
                </Rozet>
              </Table.Td>
              <Table.Td>{tarihGoster(u.baslangicTarihi)}</Table.Td>
              <Table.Td>
                {u.eksik ? (
                  <Rozet anlam="gecikmis" testId={`kurul-uye-eksik-${u.personId}`}>
                    Eksik: {u.eksikSartlar.join(', ')}
                  </Rozet>
                ) : (
                  <Rozet anlam="olumlu">Şartlar tamam</Rozet>
                )}
                {!u.hesapVar && (
                  <Text size="xs" c="dimmed">
                    Hesabı yok — davet almaz
                  </Text>
                )}
              </Table.Td>
            </Table.Tr>
          ))}
        </Table.Tbody>
      </Table>
    </Bolum>
  );
}

/** Hata bandı üye tablosunun ÜSTÜNDE (D1 — sessiz yutulmaz); satır tablonun içinde. */
function UyeEkleHatasi({
  children,
}: {
  children: (setHata: (m: string | null) => void) => React.ReactNode;
}): React.JSX.Element {
  const [hata, setHata] = useState<string | null>(null);
  return (
    <>
      {hata && (
        <Alert color={ANLAM.engel} role="alert" data-testid="kurul-uye-ekle-hata" mb="xs">
          {hata}
        </Alert>
      )}
      {children(setHata)}
    </>
  );
}

/**
 * G3.1 — KURUL SAYFASINDAN ÜYE EKLEME (gezinti artçısı 07.09.2026).
 * ⚠️ **KAVRAM DEĞİŞMEZ:** bu satır kişi kartındaki bölümle AYNI görevlendirme
 * kaydını yazar — aynı kanca (`useGorevlendirmeEkle` → `POST
 * persons/:id/designations` + `kurulUyeTipi`), aynı kurallar (alt tip zorunlu ·
 * temsilci alt tipi yürürlükteki temsilci görevlendirmesine atıf · 422'ler).
 * İKİNCİ ÜYE TABLOSU YOK; bu bir yüzey kısayoludur. Aday listesi ve tür
 * nüshası SUNUCUDAN (`uyeAdaylari` · `uyelikTuruId`); görünürlük `yetkiler.
 * uyeEkleyebilir` (görevlendirme yazma şapkası ∧ bahçe — tek yüklem).
 */
function UyeEkleSatiri({
  kurul,
  uyelikTuruId,
  onHata,
}: {
  kurul: KurulDetay;
  uyelikTuruId: string;
  onHata: (m: string | null) => void;
}): React.JSX.Element {
  const [personId, setPersonId] = useState<string | null>(null);
  const [uyeTipi, setUyeTipi] = useState<KurulUyeTipi | null>(null);
  const aday = kurul.uyeAdaylari.find((a) => a.personId === personId) ?? null;
  const ekle = useGorevlendirmeEkle(personId ?? '');
  // Madde 302: üyelik başlangıcı SAHA GÜNÜ (sunucudan), tarayıcı günü değil.
  const sahaGunu = useSahaGunu();
  const dokunuldu = personId !== null || uyeTipi !== null;
  // P12 (madde 224) — kaydedilmemiş kurul üyesi satırı çıkışta uyarır (yönlendirici tek noktada).
  useKaydedilmemisIcerik(dokunuldu ? 1 : 0, 'kurul üyesi satırı');
  const kaydedilebilir = aday !== null && uyeTipi !== null;
  return (
    <Table data-testid="kurul-uye-ekle-tablosu" mb="xs">
      <Table.Tbody>
        <SolukSatir
          testId="kurul-uye-ekle-satiri"
          dokunuldu={dokunuldu}
          kaydedilebilir={kaydedilebilir && sahaGunu.data !== undefined}
          kaydediliyor={ekle.isPending}
          kolonSayisi={3}
          onKaydet={() => {
            if (!aday || !uyeTipi || !sahaGunu.data) return;
            onHata(null);
            ekle.mutate(
              {
                employmentId: aday.employmentId,
                gorevlendirmeTuruId: uyelikTuruId,
                baslangicTarihi: sahaGunu.data,
                kurulUyeTipi: uyeTipi,
              },
              {
                onSuccess: () => {
                  setPersonId(null);
                  setUyeTipi(null);
                },
                onError: (e) => onHata(e.message),
              },
            );
          }}
        >
          <Table.Td>
            <Select
              size="xs"
              searchable
              comboboxProps={{ withinPortal: true }}
              placeholder="Kişi ara (işyerinin çalışanı)"
              aria-label="Üye adayı"
              data-testid="kurul-uye-aday-secim"
              value={personId}
              onChange={setPersonId}
              data={kurul.uyeAdaylari.map((a) => ({ value: a.personId, label: a.adSoyad }))}
              nothingFoundMessage="Aday yok"
            />
          </Table.Td>
          <Table.Td>
            <Select
              size="xs"
              comboboxProps={{ withinPortal: true }}
              placeholder="Üye tipi"
              aria-label="Üye tipi"
              data-testid="kurul-uye-tipi-secim"
              value={uyeTipi}
              onChange={(d) => setUyeTipi((d as KurulUyeTipi | null) ?? null)}
              data={KURUL_UYE_TIPLERI.map((t) => ({ value: t, label: KURUL_UYE_TIPI_ETIKETI[t] }))}
            />
          </Table.Td>
        </SolukSatir>
      </Table.Tbody>
    </Table>
  );
}

// ── KATILIM (C3) — yeter sayı türetilir ───────────────────────────────────

export function KatilimBolumu({ toplanti }: { toplanti: ToplantiDetay }): React.JSX.Element {
  const yaz = useKatilimYaz();
  const [secim, setSecim] = useState<Set<string>>(
    () => new Set(toplanti.katilim.filter((k) => k.katildi).map((k) => k.designationId)),
  );
  const [dokunuldu, setDokunuldu] = useState(false);
  const y = toplanti.yeterSayi;
  return (
    <Bolum baslik="Katılım" testId="kurul-katilim">
      {yaz.error && (
        <Alert color={ANLAM.engel} role="alert" data-testid="kurul-katilim-hata" mb="xs">
          {yaz.error.message}
        </Alert>
      )}
      <Group mb="xs" gap="md">
        <Rozet anlam={y.saglandi ? 'olumlu' : 'engel'} testId="kurul-yeter-sayi">
          Yeter sayı {y.katilan}/{y.uyeTamSayisi} (en az {y.saltCogunluk}) —{' '}
          {y.saglandi ? 'sağlandı' : 'sağlanmadı'}
        </Rozet>
      </Group>
      <Table data-testid="kurul-katilim-tablosu">
        <Table.Thead>
          <Table.Tr>
            <Table.Th w={80}>Katıldı</Table.Th>
            <Table.Th>Ad Soyad</Table.Th>
            <Table.Th>Üye tipi</Table.Th>
            <Table.Th w={110}>Rol</Table.Th>
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          {toplanti.katilim.map((k) => (
            <Table.Tr key={k.designationId} data-testid={`kurul-katilim-${k.personId}`}>
              <Table.Td>
                {toplanti.yetkiler.yazabilir ? (
                  <Checkbox
                    aria-label={`${k.adSoyad} katıldı`}
                    data-testid={`kurul-katilim-kutu-${k.personId}`}
                    checked={secim.has(k.designationId)}
                    onChange={(e) => {
                      // ⚠️ `e.currentTarget` React olayı bitince NULL'dur — güncelleyici
                      // içinde okunmaz (tur 8 / F5 dersi; yol testi bunu ilk koşuda buldu:
                      // kutu görünüyor, tık sessizce düşüyordu — §2.40).
                      const isaretli = e.currentTarget.checked;
                      setDokunuldu(true);
                      setSecim((s) => {
                        const yeni = new Set(s);
                        if (isaretli) yeni.add(k.designationId);
                        else yeni.delete(k.designationId);
                        return yeni;
                      });
                    }}
                  />
                ) : (
                  <Text size="sm">{k.katildi ? 'Evet' : 'Hayır'}</Text>
                )}
              </Table.Td>
              <Table.Td>{k.adSoyad}</Table.Td>
              <Table.Td>{KURUL_UYE_TIPI_ETIKETI[k.uyeTipi]}</Table.Td>
              <Table.Td>{KURUL_UYE_ROL_ETIKETI[k.rol]}</Table.Td>
            </Table.Tr>
          ))}
        </Table.Tbody>
      </Table>
      {toplanti.durum === 'planlandi' && (
        <KapiSebebi
          durum={toplanti.yetkiler.yazmaKapisi}
          nesne="Katılımı"
          fiil="işaretler"
          testId="kurul-katilim-yazamama-sebebi"
        />
      )}
      {toplanti.yetkiler.yazabilir && (
        <Group justify="flex-end" mt="xs">
          <Button
            size="xs"
            data-testid="kurul-katilim-kaydet"
            disabled={!dokunuldu}
            loading={yaz.isPending}
            onClick={() =>
              yaz.mutate(
                { id: toplanti.id, katilanDesignationIds: [...secim] },
                { onSuccess: () => setDokunuldu(false) },
              )
            }
          >
            Katılımı kaydet
          </Button>
        </Group>
      )}
    </Bolum>
  );
}

// ── GÜNDEM (C6) — #1 türetilmiş + maddeler + kararlar (C7) ───────────────

export function OncekiKararlarBolumu({ toplanti }: { toplanti: ToplantiDetay }): React.JSX.Element {
  return (
    <Bolum baslik="Gündem 1 — önceki toplantı kararlarının durumu" testId="kurul-onceki-kararlar">
      {toplanti.oncekiKararlar.length === 0 ? (
        <Text size="sm" c="dimmed">
          Önceki toplantıdan devreden karar yok.
        </Text>
      ) : (
        <Table data-testid="kurul-onceki-karar-tablosu">
          <Table.Thead>
            <Table.Tr>
              <Table.Th>Önceki toplantı</Table.Th>
              <Table.Th>Karar</Table.Th>
              <Table.Th>DÖF</Table.Th>
              <Table.Th w={110}>Termin</Table.Th>
              <Table.Th w={90}>Durum</Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {toplanti.oncekiKararlar.map((k) => (
              <Table.Tr key={k.kararId} data-testid={`kurul-onceki-karar-${k.kararId}`}>
                <Table.Td>
                  {k.toplantiTakipNo} ({tarihGoster(k.toplantiTarihi)})
                </Table.Td>
                <Table.Td>{k.kararMetni}</Table.Td>
                <Table.Td>
                  <Link to={`/aksiyonlar/${k.actionId}`}>{k.dofBaslik}</Link>
                </Table.Td>
                <Table.Td>{k.dofTermin ? tarihGoster(k.dofTermin) : '—'}</Table.Td>
                <Table.Td>
                  <Rozet
                    anlam={k.acik ? 'engel' : 'olumlu'}
                    testId={`kurul-onceki-karar-durum-${k.kararId}`}
                  >
                    {k.acik ? 'Açık' : 'Kapalı'}
                  </Rozet>
                </Table.Td>
              </Table.Tr>
            ))}
          </Table.Tbody>
        </Table>
      )}
    </Bolum>
  );
}

function kaynakYolu(kaynak: NonNullable<GundemMaddesi['kaynak']>): string {
  if (kaynak.tip === 'saha_turu') return `/saha-turlari/${kaynak.id}`;
  if (kaynak.tip === 'olay') return `/olaylar/${kaynak.id}`;
  return `/uygunsuzluklar/${kaynak.id}`;
}

export function GundemBolumu({ toplanti }: { toplanti: ToplantiDetay }): React.JSX.Element {
  const ekle = useGundemMaddesiEkle();
  const sil = useGundemMaddesiSil();
  const [baslik, setBaslik] = useState('');
  // P12 (madde 224) — kaydedilmemiş gündem maddesi çıkışta uyarır (yönlendirici tek noktada).
  useKaydedilmemisIcerik(baslik.trim().length > 0 ? 1 : 0, 'gündem maddesi');
  const hata = ekle.error ?? sil.error;
  return (
    <Bolum baslik="Gündem" testId="kurul-gundem">
      {hata && (
        <Alert color={ANLAM.engel} role="alert" data-testid="kurul-gundem-hata" mb="xs">
          {hata.message}
        </Alert>
      )}
      <Table data-testid="kurul-gundem-tablosu">
        <Table.Thead>
          <Table.Tr>
            <Table.Th w={40}>#</Table.Th>
            <Table.Th>Madde</Table.Th>
            <Table.Th w={160}>Kaynak</Table.Th>
            <Table.Th w={60} />
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          {toplanti.gundem.map((g) => (
            <Table.Tr key={g.id} data-testid={`kurul-gundem-${g.id}`}>
              <Table.Td>{g.sira}</Table.Td>
              <Table.Td>{g.baslik}</Table.Td>
              <Table.Td>
                {g.kaynak ? (
                  <Link to={kaynakYolu(g.kaynak)} data-testid={`kurul-gundem-kaynak-${g.id}`}>
                    {g.kaynak.takipNo}
                  </Link>
                ) : (
                  '—'
                )}
              </Table.Td>
              <Table.Td>
                {toplanti.yetkiler.yazabilir && g.kararlar.length === 0 && (
                  <SatirEylemleri
                    testId={`kurul-gundem-eylemler-${g.id}`}
                    digerleri={[
                      {
                        etiket: 'Maddeyi sil',
                        tehlikeli: true,
                        testId: `kurul-gundem-sil-${g.id}`,
                        onClick: () => sil.mutate({ id: toplanti.id, itemId: g.id }),
                      },
                    ]}
                  />
                )}
              </Table.Td>
            </Table.Tr>
          ))}
          {toplanti.yetkiler.yazabilir && (
            <SolukSatir
              testId="kurul-gundem-soluk"
              dokunuldu={baslik.trim().length > 0}
              kaydedilebilir={baslik.trim().length > 0}
              kaydediliyor={ekle.isPending}
              kolonSayisi={3}
              onKaydet={() =>
                ekle.mutate(
                  { id: toplanti.id, madde: { baslik: baslik.trim() } },
                  { onSuccess: () => setBaslik('') },
                )
              }
            >
              <Table.Td />
              <Table.Td colSpan={2}>
                <TextInput
                  size="xs"
                  placeholder="+ Gündem maddesi ekle"
                  aria-label="Yeni gündem maddesi"
                  value={baslik}
                  onChange={(e) => setBaslik(e.currentTarget.value)}
                  data-testid="kurul-gundem-baslik"
                />
              </Table.Td>
            </SolukSatir>
          )}
        </Table.Tbody>
      </Table>
      {toplanti.durum === 'planlandi' && (
        <KapiSebebi
          durum={toplanti.yetkiler.yazmaKapisi}
          nesne="Gündem maddesini"
          fiil="ekler"
          testId="kurul-gundem-yazamama-sebebi"
        />
      )}
    </Bolum>
  );
}

// ── KARARLAR (C7 · D9) ────────────────────────────────────────────────────

export function KararlarBolumu({ toplanti }: { toplanti: ToplantiDetay }): React.JSX.Element {
  const yaz = useKararYaz();
  const duzelt = useKararDuzelt();
  const [maddeId, setMaddeId] = useState<string | null>(null);
  const [metin, setMetin] = useState('');
  // P12 (madde 224) — kaydedilmemiş kurul kararı çıkışta uyarır (yönlendirici tek noktada).
  useKaydedilmemisIcerik(maddeId !== null || metin.length > 0 ? 1 : 0, 'kurul kararı');
  const [not, setNot] = useState('');
  const [ilan, setIlan] = useState(false);
  const hata = yaz.error ?? duzelt.error;
  const kararlar = toplanti.gundem.flatMap((g) => g.kararlar.map((k) => ({ g, k })));
  // T2 — sonuç SORULMAZ, karar metninden türetilir: metin yoksa satır *görüşüldü*
  // (görüşme notu yeter); ilan yalnız alınan karara (sunucuyla aynı yüklem).
  const alinan = kurulKararSonucu({ kararMetni: metin }) === 'alindi';
  const kaydedilebilir =
    maddeId !== null && (metin.trim().length > 0 || not.trim().length > 0) && (alinan || !ilan);
  return (
    <Bolum baslik="Kararlar" testId="kurul-kararlar">
      {hata && (
        <Alert color={ANLAM.engel} role="alert" data-testid="kurul-karar-hata" mb="xs">
          {hata.message}
        </Alert>
      )}
      {/* KARAR ENGELİ SUNUCUDAN — yeter sayı sağlanmadıysa yazma alanı HİÇ çizilmez, sebebi yazılır (§2.40 · G5). */}
      {toplanti.yetkiler.yazabilir &&
        !toplanti.yetkiler.kararYazilabilir &&
        toplanti.yetkiler.kararEngeli && (
          <Text size="sm" c="dimmed" data-testid="kurul-karar-engeli" mb="xs">
            {toplanti.yetkiler.kararEngeli}
          </Text>
        )}
      {toplanti.durum === 'planlandi' && (
        <KapiSebebi
          durum={toplanti.yetkiler.yazmaKapisi}
          nesne="Kararı"
          fiil="yazar"
          testId="kurul-karar-yazamama-sebebi"
        />
      )}
      <Table data-testid="kurul-karar-tablosu">
        <Table.Thead>
          <Table.Tr>
            <Table.Th w={60}>Madde</Table.Th>
            <Table.Th>Karar</Table.Th>
            <Table.Th w={70}>İlan</Table.Th>
            <Table.Th w={200}>DÖF</Table.Th>
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          {kararlar.map(({ g, k }) => (
            <KararSatiri key={k.id} toplanti={toplanti} madde={g} karar={k} />
          ))}
          {toplanti.yetkiler.kararYazilabilir && (
            <SolukSatir
              testId="kurul-karar-soluk"
              dokunuldu={maddeId !== null || metin.length > 0}
              kaydedilebilir={kaydedilebilir}
              kaydediliyor={yaz.isPending}
              kolonSayisi={3}
              onKaydet={() => {
                if (!kaydedilebilir || maddeId === null) return;
                yaz.mutate(
                  {
                    id: toplanti.id,
                    karar: {
                      agendaItemId: maddeId,
                      kararMetni: metin.trim() || null,
                      gorusmeNotu: not.trim() || null,
                      ilan: alinan && ilan,
                    },
                  },
                  {
                    onSuccess: () => {
                      setMetin('');
                      setNot('');
                      setIlan(false);
                    },
                  },
                );
              }}
            >
              <Table.Td>
                <Select
                  size="xs"
                  comboboxProps={{ withinPortal: true }}
                  aria-label="Gündem maddesi"
                  placeholder="Madde"
                  data-testid="kurul-karar-madde"
                  value={maddeId}
                  onChange={setMaddeId}
                  data={toplanti.gundem.map((g) => ({
                    value: g.id,
                    label: `${g.sira}. ${g.baslik}`,
                  }))}
                />
              </Table.Td>
              <Table.Td>
                <Stack gap={4}>
                  <TextInput
                    size="xs"
                    placeholder="+ Karar metni"
                    aria-label="Karar metni"
                    value={metin}
                    onChange={(e) => setMetin(e.currentTarget.value)}
                    data-testid="kurul-karar-metin"
                  />
                  <TextInput
                    size="xs"
                    placeholder="Görüşme notu"
                    aria-label="Görüşme notu"
                    value={not}
                    onChange={(e) => setNot(e.currentTarget.value)}
                    data-testid="kurul-karar-not"
                  />
                </Stack>
              </Table.Td>
              <Table.Td>
                <Switch
                  size="xs"
                  aria-label="İlan edilsin"
                  data-testid="kurul-karar-ilan"
                  checked={alinan && ilan}
                  disabled={!alinan}
                  onChange={(e) => setIlan(e.currentTarget.checked)}
                />
              </Table.Td>
            </SolukSatir>
          )}
        </Table.Tbody>
      </Table>
    </Bolum>
  );
}

function KararSatiri({
  toplanti,
  madde,
  karar,
}: {
  toplanti: ToplantiDetay;
  madde: GundemMaddesi;
  karar: Karar;
}): React.JSX.Element {
  const dofAc = useKararDofAc();
  const duzelt = useKararDuzelt();
  const [dofAcik, setDofAcik] = useState(false);
  const personel = useAtanabilirler({ enabled: dofAcik });
  const [duzeltmeAcik, setDuzeltmeAcik] = useState(false);
  const [baslik, setBaslik] = useState((karar.kararMetni ?? '').slice(0, 120));
  const [atanan, setAtanan] = useState<string | null>(null);
  const [termin, setTermin] = useState('');
  const [yeniMetin, setYeniMetin] = useState(karar.kararMetni ?? '');
  const [gerekce, setGerekce] = useState('');
  // ✏️ P13 — DÖF düğmesi aksiyon açma kapısından (`dofKapisi`: kurul kapısı +
  // `uzman_aksiyon_atama`); eskiden düzeltme bayrağına bakıyor, ayarı okumuyordu.
  const dofAcilabilir =
    karar.actionId === null &&
    kurulKararSonucu(karar) === 'alindi' &&
    toplanti.yetkiler.dofKapisi.yazabilir;
  return (
    <>
      <Table.Tr data-testid={`kurul-karar-${karar.id}`}>
        <Table.Td>{madde.sira}</Table.Td>
        {/* T2 — SONUÇ KOLONU YOK: karar ana metin, görüşme notu altında soluk alt
            satır (aksiyon listesinin kaynak/referans deseni); metin yoksa satır
            *görüşüldü*dür ve bunu kendisi söyler. */}
        <Table.Td>
          <Stack gap={0}>
            {kurulKararSonucu(karar) === 'alindi' ? (
              <Text size="sm" data-testid={`kurul-karar-metin-${karar.id}`}>
                {karar.kararMetni}
              </Text>
            ) : (
              <Text size="sm" c="dimmed" data-testid={`kurul-karar-gorusuldu-${karar.id}`}>
                {KURUL_KARAR_SONUC_ETIKETI.gorusuldu}
              </Text>
            )}
            {karar.gorusmeNotu && (
              <Text size="xs" c="dimmed" data-testid={`kurul-karar-not-${karar.id}`}>
                {karar.gorusmeNotu}
              </Text>
            )}
          </Stack>
        </Table.Td>
        <Table.Td>{karar.ilan ? 'Evet' : '—'}</Table.Td>
        <Table.Td>
          {karar.actionId ? (
            <Group gap={6} wrap="nowrap">
              <Link
                to={`/aksiyonlar/${karar.actionId}`}
                data-testid={`kurul-karar-dof-${karar.id}`}
              >
                {karar.dofBaslik}
              </Link>
              <Rozet anlam={karar.acik ? 'engel' : 'olumlu'}>
                {karar.acik ? 'Açık' : 'Kapalı'}
              </Rozet>
            </Group>
          ) : (
            <Group gap={6} wrap="nowrap">
              <Text size="xs" c="dimmed">
                —
              </Text>
              {dofAcilabilir && (
                <Button
                  size="compact-xs"
                  variant="light"
                  data-testid={`kurul-karar-dof-ac-${karar.id}`}
                  onClick={() => setDofAcik((a) => !a)}
                >
                  DÖF aç
                </Button>
              )}
              {toplanti.yetkiler.duzeltebilir && (
                <Button
                  size="compact-xs"
                  variant="subtle"
                  data-testid={`kurul-karar-duzelt-${karar.id}`}
                  onClick={() => setDuzeltmeAcik((a) => !a)}
                >
                  Düzelt
                </Button>
              )}
            </Group>
          )}
        </Table.Td>
      </Table.Tr>
      {dofAcik && (
        <Table.Tr data-testid={`kurul-karar-dof-formu-${karar.id}`}>
          <Table.Td colSpan={4}>
            {dofAc.error && (
              <Alert color={ANLAM.engel} role="alert" data-testid="kurul-karar-dof-hata" mb="xs">
                {dofAc.error.message}
              </Alert>
            )}
            <Group align="flex-end" wrap="wrap">
              <TextInput
                size="xs"
                label="DÖF başlığı"
                w={280}
                value={baslik}
                onChange={(e) => setBaslik(e.currentTarget.value)}
                data-testid={`kurul-karar-dof-baslik-${karar.id}`}
              />
              <Select
                size="xs"
                label="Sorumlu"
                w={240}
                searchable
                comboboxProps={{ withinPortal: true }}
                data-testid={`kurul-karar-dof-atanan-${karar.id}`}
                value={atanan}
                onChange={setAtanan}
                data={(personel.data ?? []).map((p) => ({ value: p.appUserId, label: p.etiket }))}
              />
              <TextInput
                size="xs"
                type="date"
                label="Termin"
                w={160}
                value={termin}
                onChange={(e) => setTermin(e.currentTarget.value)}
                data-testid={`kurul-karar-dof-termin-${karar.id}`}
              />
              <Button
                size="xs"
                data-testid={`kurul-karar-dof-kaydet-${karar.id}`}
                disabled={!baslik.trim() || !atanan || !termin}
                loading={dofAc.isPending}
                onClick={() =>
                  atanan &&
                  dofAc.mutate(
                    {
                      kararId: karar.id,
                      girdi: {
                        baslik: baslik.trim(),
                        atananAppUserId: atanan,
                        terminTarihi: termin,
                      },
                    },
                    { onSuccess: () => setDofAcik(false) },
                  )
                }
              >
                DÖF aç
              </Button>
              <Button size="xs" variant="default" onClick={() => setDofAcik(false)}>
                Vazgeç
              </Button>
            </Group>
          </Table.Td>
        </Table.Tr>
      )}
      {duzeltmeAcik && (
        <Table.Tr data-testid={`kurul-karar-duzeltme-formu-${karar.id}`}>
          <Table.Td colSpan={4}>
            {duzelt.error && (
              <Alert
                color={ANLAM.engel}
                role="alert"
                data-testid="kurul-karar-duzeltme-hata"
                mb="xs"
              >
                {duzelt.error.message}
              </Alert>
            )}
            <Stack gap="xs">
              <Textarea
                size="xs"
                label="Karar metni"
                autosize
                value={yeniMetin}
                onChange={(e) => setYeniMetin(e.currentTarget.value)}
                data-testid={`kurul-karar-duzeltme-metin-${karar.id}`}
              />
              {toplanti.durum !== 'planlandi' && (
                <TextInput
                  size="xs"
                  label="Gerekçe (kapanmış tutanakta zorunlu; düzeltme izi tutanağa basılır)"
                  value={gerekce}
                  onChange={(e) => setGerekce(e.currentTarget.value)}
                  data-testid={`kurul-karar-duzeltme-gerekce-${karar.id}`}
                />
              )}
              <Group>
                <Button
                  size="xs"
                  data-testid={`kurul-karar-duzeltme-kaydet-${karar.id}`}
                  loading={duzelt.isPending}
                  disabled={
                    (!yeniMetin.trim() && !karar.gorusmeNotu) ||
                    (toplanti.durum !== 'planlandi' && !gerekce.trim())
                  }
                  onClick={() =>
                    duzelt.mutate(
                      {
                        kararId: karar.id,
                        yama: {
                          kararMetni: yeniMetin.trim() || null,
                          gerekce: gerekce.trim() || null,
                        },
                      },
                      { onSuccess: () => setDuzeltmeAcik(false) },
                    )
                  }
                >
                  Düzeltmeyi kaydet
                </Button>
                <Button size="xs" variant="default" onClick={() => setDuzeltmeAcik(false)}>
                  Vazgeç
                </Button>
              </Group>
            </Stack>
          </Table.Td>
        </Table.Tr>
      )}
    </>
  );
}

// ── KAPANIŞ (C3) ──────────────────────────────────────────────────────────

export function KapanisBolumu({
  toplanti,
  onKapandi,
  onVazgec,
}: {
  toplanti: ToplantiDetay;
  onKapandi: () => void;
  onVazgec: () => void;
}): React.JSX.Element {
  const kapat = useToplantiKapat();
  const [not, setNot] = useState('');
  const yapildi = toplanti.yetkiler.yapildiKapatilabilir;
  return (
    <Bolum
      baslik={yapildi ? 'Toplantıyı kapat' : 'Toplantı yapılamadı tutanağı'}
      aciklama={
        yapildi
          ? 'Kapanışla kararlar kilitlenir; ilan işaretli kararlar çalışanlara duyurulur.'
          : 'Yeter sayı sağlanmadığı için toplantı yalnız “yapılamadı” olarak kapanır; karar yazılamaz.'
      }
      testId="kurul-kapanis"
    >
      {kapat.error && (
        <Alert color={ANLAM.engel} role="alert" data-testid="kurul-kapanis-hata" mb="xs">
          {kapat.error.message}
        </Alert>
      )}
      <Stack gap="sm">
        <Textarea
          size="xs"
          label="Kapanış notu"
          autosize
          value={not}
          onChange={(e) => setNot(e.currentTarget.value)}
          data-testid="kurul-kapanis-notu"
        />
        <Group justify="flex-end">
          <Button variant="default" onClick={onVazgec} data-testid="kurul-kapanis-vazgec">
            Vazgeç
          </Button>
          <Button
            data-testid="kurul-kapanis-onayla"
            loading={kapat.isPending}
            onClick={() =>
              kapat.mutate(
                {
                  id: toplanti.id,
                  girdi: {
                    sonuc: yapildi ? 'yapildi' : 'yapilamadi',
                    kapanisNotu: not.trim() || null,
                  },
                },
                { onSuccess: onKapandi },
              )
            }
          >
            {yapildi ? 'Yapıldı olarak kapat' : 'Yapılamadı olarak kapat'}
          </Button>
        </Group>
      </Stack>
    </Bolum>
  );
}

// ── İMZALI NÜSHA (C9) + İZ (D9) ───────────────────────────────────────────

export function NushaBolumu({ toplanti }: { toplanti: ToplantiDetay }): React.JSX.Element {
  const yukle = useToplantiNushaYukle();
  const sil = useToplantiNushaSil();
  const indir = useCiktiIndir();
  const hata = yukle.error ?? sil.error;
  // Madde 230 — kapalı toplantıda silme GEREKÇE ister; bayrak sunucudan
  // (`nushaSilmeGerekceZorunlu`), ekran "kapalı mı" kuralını yeniden yazmaz.
  const [silinecek, setSilinecek] = useState<string | null>(null);
  const [gerekce, setGerekce] = useState('');
  const gerekceZorunlu = toplanti.yetkiler.nushaSilmeGerekceZorunlu;
  return (
    <Bolum baslik="İmzalı nüsha" testId="kurul-nusha">
      {hata && (
        <Alert color={ANLAM.engel} role="alert" data-testid="kurul-nusha-hata" mb="xs">
          {hata.message}
        </Alert>
      )}
      <Stack gap="xs">
        {toplanti.ekler.length === 0 && (
          <Text size="sm" c="dimmed">
            İmzalı nüsha yüklenmedi.
          </Text>
        )}
        {toplanti.ekler.map((e) => (
          <Group key={e.id} gap="sm" data-testid={`kurul-nusha-${e.id}`}>
            <Button
              size="compact-xs"
              variant="subtle"
              onClick={() =>
                indir.mutate({
                  yol: `/api/committee-meetings/${toplanti.id}/attachments/${e.id}/download`,
                  yedekAd: e.dosyaAdi,
                })
              }
            >
              {e.dosyaAdi}
            </Button>
            <Text size="xs" c="dimmed">
              {zamanGoster(e.createdAt)}
            </Text>
            {/* P13-c (madde 227) — silme kapısı SATIR BAŞINA sunucudan: kurul yazma →
                kapalı toplantı → sahiplik (yükleyen VEYA holding yön.). */}
            {e.silmeKapisi.yazabilir && silinecek !== e.id && (
              <Button
                size="compact-xs"
                variant="subtle"
                color={ANLAM.engel}
                data-testid={`kurul-nusha-kaldir-${e.id}`}
                onClick={() =>
                  gerekceZorunlu
                    ? setSilinecek(e.id)
                    : sil.mutate({ id: toplanti.id, attachmentId: e.id })
                }
              >
                Kaldır
              </Button>
            )}
            {silinecek === e.id && (
              <Group gap="xs" wrap="nowrap" data-testid="kurul-nusha-gerekce-satiri">
                <TextInput
                  size="xs"
                  placeholder="Kaldırma gerekçesi (zorunlu)"
                  aria-label="Kaldırma gerekçesi"
                  value={gerekce}
                  onChange={(ev) => setGerekce(ev.currentTarget.value)}
                  data-testid="kurul-nusha-gerekce"
                  style={{ flex: 1 }}
                />
                <Button
                  size="xs"
                  color={ANLAM.engel}
                  data-testid={`kurul-nusha-kaldir-onayla-${e.id}`}
                  disabled={gerekce.trim().length < 3}
                  loading={sil.isPending}
                  onClick={() =>
                    sil.mutate(
                      { id: toplanti.id, attachmentId: e.id, gerekce: gerekce.trim() },
                      {
                        onSuccess: () => {
                          setSilinecek(null);
                          setGerekce('');
                        },
                      },
                    )
                  }
                >
                  Kaldır
                </Button>
                <Button size="xs" variant="subtle" onClick={() => setSilinecek(null)}>
                  Vazgeç
                </Button>
              </Group>
            )}
          </Group>
        ))}
        {/* Madde 230 — kaldırılan nüshaların izi (kim · ne zaman · gerekçe); sunucudan. */}
        {toplanti.silinenEkler.map((s) => (
          <Text key={s.id} size="xs" c="dimmed" data-testid={`kurul-nusha-silinen-${s.id}`}>
            Kaldırıldı: {s.dosyaAdi} — {s.silenEtiket ?? 'Hesap'} · {zamanGoster(s.silindiAt)}
            {s.gerekce ? ` · ${s.gerekce}` : ''}
          </Text>
        ))}
        {/* Sebep bölüm başına BİR KEZ — ilk kapalı satırın sebebi. ⚠️ Nüsha yalnız
            kapalı toplantıya yüklenir ve toplantı yeniden açılmaz → yüklenen nüsha
            kalıcıdır; cümle bunu söyler ("Kapalı toplantıda nüsha silinemez"). */}
        <SatirSilmeSebebi
          kapilar={toplanti.ekler.map((e) => e.silmeKapisi)}
          nesne="Nüshayı"
          fiil="kaldırır"
          testId="kurul-nusha-silme-sebebi"
        />
        {toplanti.yetkiler.ekYukleyebilir && (
          <FileButton
            onChange={(d) => d && yukle.mutate({ id: toplanti.id, dosya: d })}
            accept="application/pdf,image/jpeg,image/png"
          >
            {(props) => (
              <Button
                {...props}
                size="xs"
                variant="light"
                data-testid="kurul-nusha-yukle"
                loading={yukle.isPending}
              >
                İmzalı nüsha yükle
              </Button>
            )}
          </FileButton>
        )}
        <KapiSebebi
          durum={toplanti.yetkiler.yazmaKapisi}
          nesne="İmzalı nüshayı"
          fiil="yükler"
          testId="kurul-nusha-yazamama-sebebi"
        />
      </Stack>
    </Bolum>
  );
}

export function IzBolumu({ toplanti }: { toplanti: ToplantiDetay }): React.JSX.Element {
  return (
    <Bolum baslik="İşlem izi" testId="kurul-iz">
      <Table>
        <Table.Tbody>
          {toplanti.log.map((l) => (
            <Table.Tr key={l.id} data-testid={`kurul-iz-${l.islem}`}>
              <Table.Td w={150}>{zamanGoster(l.createdAt)}</Table.Td>
              <Table.Td w={140}>{IZ_ETIKETI[l.islem] ?? l.islem}</Table.Td>
              <Table.Td>{l.aktorEtiket ?? '—'}</Table.Td>
              <Table.Td>{l.gerekce ?? ''}</Table.Td>
            </Table.Tr>
          ))}
        </Table.Tbody>
      </Table>
    </Bolum>
  );
}

const IZ_ETIKETI: Record<string, string> = {
  plan: 'Planlandı',
  plan_duzeltme: 'Plan düzeltildi',
  katilim: 'Katılım kaydedildi',
  kapanis: 'Kapatıldı',
  karar_duzeltme: 'Karar düzeltildi',
  dof_acildi: 'DÖF açıldı',
};

// ── C6 — "KURUL GÜNDEMİNE EKLE" (olay · uygunsuzluk · saha turu detayları) ─

/**
 * Görünürlük ve engel SUNUCUDAN (`gundem-secenekleri` — tek yüklem, §2.40):
 * kurul yoksa düğme GÖRÜNMEZ; planlı toplantı yoksa PASİF + sebep.
 */
export function GundemineEkleDugmesi({
  workplaceId,
  baslik,
  kaynak,
  testId,
}: {
  workplaceId: string;
  baslik: string;
  kaynak: { siteTourId?: string; incidentId?: string; nonconformityId?: string };
  testId: string;
}): React.JSX.Element | null {
  const secenekler = useGundemSecenekleri(workplaceId);
  const ekle = useGundemMaddesiEkle();
  const [acik, setAcik] = useState(false);
  const [toplantiId, setToplantiId] = useState<string | null>(null);
  const [eklenen, setEklenen] = useState<string | null>(null);
  if (!secenekler.isSuccess || secenekler.data.kurulId === null) return null;
  const s = secenekler.data;
  return (
    <Group gap="xs" align="center">
      {!s.ekleyebilir && s.engel && (
        <Text size="xs" c="dimmed" data-testid={`${testId}-engeli`}>
          {s.engel}
        </Text>
      )}
      <Button
        size="xs"
        variant="light"
        data-testid={testId}
        disabled={!s.ekleyebilir}
        onClick={() => setAcik((a) => !a)}
      >
        Kurul gündemine ekle
      </Button>
      {acik && s.ekleyebilir && (
        <Group gap="xs" data-testid={`${testId}-secim`}>
          <Select
            size="xs"
            w={260}
            comboboxProps={{ withinPortal: true }}
            aria-label="Toplantı"
            placeholder="Planlı toplantı seç"
            data-testid={`${testId}-toplanti`}
            value={toplantiId}
            onChange={setToplantiId}
            data={s.toplantilar.map((t) => ({
              value: t.id,
              label: `${t.takipNo} — ${tarihGoster(t.tarih)}`,
            }))}
          />
          <Button
            size="xs"
            data-testid={`${testId}-onayla`}
            disabled={!toplantiId}
            loading={ekle.isPending}
            onClick={() =>
              toplantiId &&
              ekle.mutate(
                { id: toplantiId, madde: { baslik, ...kaynak } },
                {
                  onSuccess: (t) => {
                    setEklenen(t.takipNo);
                    setAcik(false);
                    setToplantiId(null);
                  },
                },
              )
            }
          >
            Ekle
          </Button>
        </Group>
      )}
      {ekle.error && (
        <Alert color={ANLAM.engel} role="alert" data-testid={`${testId}-hata`}>
          {ekle.error.message}
        </Alert>
      )}
      {eklenen && (
        <Text size="xs" c="dimmed" data-testid={`${testId}-eklendi`}>
          {eklenen} gündemine eklendi
        </Text>
      )}
    </Group>
  );
}
